import PERMISSIONS from '@/constants/permissions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { PartnerTranslationModel } from '@/typings/api/emailCenter';
import { useHasAccess } from '@/utils/hooks';
import { F, getIntlTranslation } from '@/utils/intl';
import { TranslationOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Button,
  ConfigProvider,
  Input,
  Popover,
  Select,
  Space,
  Tooltip,
} from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl, useSelector } from 'umi';
import { languageLabel } from '../constants';
import {
  resolveTranslations,
  translationGroups,
  translationKeys,
} from '../translationCatalog';
import { useSaveTranslation } from '../useSaveTranslation';
import {
  buildVariableSnippet,
  insertAtCursor,
} from './EmailBodyEditor/insertion';
import {
  bindingExpression,
  DEFAULT_GROUP,
  normalizeKeyPart,
  parseBinding,
  resolveBinding,
  suggestKeyName,
} from './EmailBodyEditor/translationBinding';
import { EmailVariableGroup } from './EmailBodyEditor/types';
import TranslationDisplay from './TranslationDisplay';
import TranslationHint from './TranslationHint';
import TranslationKeyOption, {
  translationSearchText,
} from './TranslationKeyOption';
import VariableInsertButton from './VariableInsertButton';
import VariableOptionList from './VariableOptionList';
import VariableTextField from './VariableTextField';
import { useVariableSuggestions } from './useVariableSuggestions';

import styles from './InputWithPartnerTranslation.less';

interface InputWithPartnerTranslationProps {
  /** The stored field value: plain words, or `{{ 'group.key'|trans }}`. */
  value?: string;
  /**
   * Absent ⇒ **wording-only**: the field cannot be re-pointed at another key or turned
   * back into plain text, because it is not a template field. That is the preview
   * sidebar, which lists the keys an email *uses* — "point this somewhere else" would be
   * editing the email from a panel that only describes it.
   */
  onChange?: (next: string) => void;
  /** The language whose wording is shown inline. */
  language: string;
  /** Names a minted key after the thing being edited (see `suggestKey`). */
  templateName?: string | null;
  fieldName?: string | null;
  placeholder?: string;
  /**
   * The **template's** field cannot be changed: plain text is not typeable, and the
   * field cannot be re-pointed at another key or turned back into plain text.
   *
   * It deliberately does not stop the *wording* from being edited. A translation
   * belongs to the partner, not to the template that happens to use it, so filling one
   * in is allowed even for a template this account may not edit — a master owned by
   * another account, or one without the draft permission.
   */
  disabled?: boolean;
  /** Renders a textarea instead of a single-line input, for a plain-text value. */
  rows?: number;
  /**
   * Offers `{{ customer.firstname }}`-style placeholders — through a picker at the end
   * of the field, and inline as soon as `{{` is typed. Omitted, the picker does not
   * appear (a field with no variables to offer should not advertise them) and the
   * inline list falls back to the partner's saved texts alone.
   *
   * Only for a plain-text value: when the field borrows its words from the catalog
   * they are the key's, and a variable belongs in the *wording*, edited per language
   * in the popover, not in the binding this field holds.
   */
  variableGroups?: EmailVariableGroup[];
  /**
   * The template's placeholders are not ours (an NL2Go body): the picker and the
   * `{{` variable list are withheld, the partner's saved texts stay.
   */
  variablesUnsupported?: boolean;
  /** Ends an undo coalescing run — the visual editor's inspector passes this. */
  onEndEditRun?: () => void;
}

/** Every language's wording for one key, `''` where the key has none. */
function wordingByLanguage(
  translations: PartnerTranslationModel[],
  key: string,
  languages: readonly string[],
): Record<string, string> {
  const draft: Record<string, string> = {};
  for (const code of languages) {
    draft[code] = resolveTranslations(translations, code)[key] ?? '';
  }
  return draft;
}

/**
 * The DOM node behind antd's `Input` / `Input.TextArea`, whichever this field
 * rendered — the caret lives there, and inserting a variable has to put it back.
 */
function fieldElement(
  ref: React.RefObject<any>,
): HTMLInputElement | HTMLTextAreaElement | null {
  const current = ref.current;
  return current?.input ?? current?.resizableTextArea?.textArea ?? null;
}

/**
 * A text field that either holds its own words or borrows them from the partner's
 * translation catalog.
 *
 * ### The two states look different, on purpose
 *
 * **Plain text** is an ordinary input: the words are this template's, so typing them is
 * the whole interaction.
 *
 * **Translated** is *not* an input. The words are not this field's — they are
 * partner-wide, shared with every template using the key — so rendering them in a text
 * box invites typing that would either rewrite copy in other emails or be silently
 * discarded. It renders as what it is: the resolved wording, with **the key it came
 * from** beside it, and a click opens the editor.
 *
 * With **no wording for the language on screen** it stays that shape rather than
 * becoming a warning box: the field shows the binding, and the `TranslationHint` strip
 * under it says the wording is missing and offers to add it — the same strip, in the
 * same place, as for a field whose value is only partly a translation.
 *
 * ### Every language at once
 *
 * The popover lists them all, not one behind a switch. A translation only makes sense
 * as a set — the question is always "which languages is this missing?" — and a dropdown
 * makes you visit each state to answer it. Empty ones are marked.
 *
 * *Which* languages is the partner's own setting (`selectTranslationLanguages`, backed
 * by `branding_manage`), not the product's full list: a partner selling in two
 * countries should not page past eleven they will never fill in. Unset means all of them.
 *
 * They save in **one request**, which is load-bearing rather than an optimisation: the
 * model merges into the catalog's copy of the value map, so separate per-language saves
 * would each drop the others.
 */
const InputWithPartnerTranslation: React.FC<
  InputWithPartnerTranslationProps
> = ({
  value,
  onChange,
  language,
  templateName,
  fieldName,
  placeholder,
  disabled,
  rows,
  variableGroups,
  variablesUnsupported,
  onEndEditRun,
}) => {
  const intl = useIntl();
  const translations = useSelector(EmailCenterSelectors.selectTranslations);
  /**
   * Not every language the product has — the ones this partner said they
   * translate into. A partner selling in two countries should not page past
   * eleven they will never fill in. Falls back to all of them when unset.
   */
  const languages = useSelector(
    EmailCenterSelectors.selectTranslationLanguages,
  );
  const wordingVariableGroups = useSelector(
    EmailCenterSelectors.selectTranslationVariableGroups,
  );
  const saveTranslation = useSaveTranslation();

  const [open, setOpen] = useState(false);
  /** Wording per language while the popover is open, saved as one batch. */
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  /**
   * The key being composed for a field that has none yet, as group and key separately —
   * that is how `partner_translations` stores it, and it is the choice a partner should
   * make: a name minted from a template title
   * (`emails.zz_xxxl_appointment_all_regions_keep.betreff`) is nobody's idea of a key,
   * and the key outlives the template that first needed it.
   */
  const [group, setGroup] = useState(DEFAULT_GROUP);
  const [keyName, setKeyName] = useState('');

  const binding = useMemo(() => parseBinding(value), [value]);
  /**
   * Re-pointing the field at a different key, unbinding it, and minting a new one all
   * rewrite the **template's** value, so they follow `disabled`. The wording does not.
   */
  const canRepoint = !!onChange && !disabled;
  /**
   * Editing the *wording* is a different permission from editing the template, and
   * until now it was not checked at all: anyone who could open a template was given
   * the thirteen-language editor and a Save button, filled it in, and got a 403 from
   * `PartnerTranslationPolicy` with the typed text discarded. The policy is the
   * authority — this only stops offering what it refuses.
   */
  const canEditWording = useHasAccess([
    PERMISSIONS.PartnerTranslations.EDIT,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);
  const inlineValues = useMemo(
    () => resolveTranslations(translations, language),
    [translations, language],
  );
  const takenKeys = useMemo(
    () => translationKeys(translations),
    [translations],
  );

  const resolved =
    binding.kind === 'bound'
      ? resolveBinding(binding.key, inlineValues)
      : { text: binding.text, missing: false };

  const inputRef = useRef<any>(null);
  /**
   * Where the caret was before the picker took focus. Opening the popover blurs the
   * field, so the selection has to be read while it is still there — remembered on
   * every interaction that can move it. Null means "never focused": the variable then
   * goes to the end, which is where someone who has not clicked into the field yet
   * would expect it.
   */
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  const rememberSelection = useCallback(() => {
    const element = fieldElement(inputRef);
    if (!element) return;
    selectionRef.current = {
      start: element.selectionStart ?? 0,
      end: element.selectionEnd ?? 0,
    };
  }, []);

  /**
   * Writes one Twig expression — `{{ namespace.name }}` from the picker, or whatever
   * the `{{` list's pick inserts (a variable, or a `|trans` lookup) — and leaves the
   * caret after it. `span` is the text it replaces: the half-typed `{{ cust` when the
   * autocomplete picked it, the selection (or the end of the field) when the picker did.
   */
  const insertSnippet = useCallback(
    (snippet: string, span?: { start: number; end: number }) => {
      if (binding.kind === 'bound') return;
      const text = binding.text;
      const selection = span ??
        selectionRef.current ?? {
          start: text.length,
          end: text.length,
        };
      const { text: next, caret } = insertAtCursor(
        text,
        snippet,
        selection.start,
        selection.end,
      );
      onChange?.(next);
      onEndEditRun?.();
      selectionRef.current = { start: caret, end: caret };
      // After the re-render, so the caret lands in the field's new value rather
      // than being clamped to the old one.
      requestAnimationFrame(() => {
        const element = fieldElement(inputRef);
        if (!element) return;
        element.focus();
        element.setSelectionRange(caret, caret);
      });
    },
    [binding, onChange, onEndEditRun],
  );

  const handleInsertVariable = useCallback(
    (namespace: string, name: string) =>
      insertSnippet(buildVariableSnippet(namespace, name)),
    [insertSnippet],
  );

  /**
   * A bound field has no input to insert into, and a read-only one may not be typed
   * in either — in both cases the picker would offer an edit that cannot happen.
   */
  const showVariablePicker =
    binding.kind !== 'bound' && !!onChange && !disabled;
  /** The picker itself, minus the templates whose references are not ours. */
  const offerVariables = showVariablePicker && !variablesUnsupported;

  /**
   * The keys the `{{` list offers, with what each one currently says. Built from the
   * memos this field already keeps for its own popover rather than read again.
   */
  const translationEntries = useMemo(
    () => takenKeys.map((key) => ({ key, text: inlineValues[key] })),
    [takenKeys, inlineValues],
  );

  /**
   * The same `{{` autocomplete the body editors run, over the same catalog — this
   * host only has to say where its caret is and what a pick replaces.
   *
   * Saved texts are offered whether or not the host passed `variableGroups`: they come
   * from the partner's catalog, not from the template's variable list, so a field with
   * no variables to advertise still has texts worth reusing.
   */
  const variables = useVariableSuggestions({
    variableGroups: variablesUnsupported ? undefined : variableGroups,
    translations: translationEntries,
    enabled: showVariablePicker,
    onAccept: (option, trigger) =>
      insertSnippet(option.snippet, {
        start: trigger.start,
        end: trigger.end,
      }),
  });

  /**
   * Reads the caret's surroundings straight off the DOM node — the field's value
   * prop is a render behind while typing, and the caret is not in it at all.
   */
  const syncTrigger = useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement) => {
      const caret = element.selectionStart ?? element.value.length;
      variables.sync(element.value.slice(0, caret));
    },
    [variables],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!variables.handleKey(event.key)) return;
      // Enter would otherwise submit the drawer's form or break the line, and
      // Escape would close the drawer — dismissing a list is not throwing the
      // email away.
      event.preventDefault();
      event.stopPropagation();
    },
    [variables],
  );

  /**
   * Opening seeds the draft. A bound field seeds from the catalog; a plain one seeds the
   * language on screen with the words already in the field, so translating never starts
   * by discarding what it said.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) return;

      if (binding.kind === 'bound') {
        setDraft(wordingByLanguage(translations, binding.key, languages));
        return;
      }

      const seeded: Record<string, string> = {};
      for (const code of languages) seeded[code] = '';
      seeded[language] = binding.text;
      setDraft(seeded);
      setGroup(DEFAULT_GROUP);
      setKeyName(suggestKeyName(templateName, fieldName));
    },
    [binding, language, translations, templateName, fieldName, languages],
  );

  /** The strip under a missing field opens the same editor its icon does. */
  const openEditor = useCallback(
    () => handleOpenChange(true),
    [handleOpenChange],
  );

  const setWording = useCallback((code: string, text: string) => {
    setDraft((current) => ({ ...current, [code]: text }));
  }, []);

  /** Only what the user actually changed, so an untouched language is never rewritten. */
  const changedWording = useCallback(
    (key: string) => {
      const stored = wordingByLanguage(translations, key, languages);
      const changed: Record<string, string> = {};
      for (const code of languages) {
        if ((draft[code] ?? '') !== stored[code])
          changed[code] = draft[code] ?? '';
      }
      return changed;
    },
    [draft, translations, languages],
  );

  /** Binds to an existing key, adopting its wording. */
  const bindTo = useCallback(
    (key: string) => {
      onChange?.(
        bindingExpression(key, binding.kind === 'bound' && binding.raw),
      );
      onEndEditRun?.();
      setDraft(wordingByLanguage(translations, key, languages));
    },
    [onChange, onEndEditRun, binding, translations, languages],
  );

  /** The key the composer currently describes, `group.key`. */
  const composedKey = useMemo(() => {
    const g = normalizeKeyPart(group) || DEFAULT_GROUP;
    const k = normalizeKeyPart(keyName);
    return k ? `${g}.${k}` : '';
  }, [group, keyName]);

  /**
   * Pointing at a key that already exists is legitimate — that is reuse — but it must
   * not *overwrite* its wording by accident, so that case binds without saving and says
   * so. A new key saves the draft and binds.
   */
  const composedExists = takenKeys.includes(composedKey);

  const handleCreate = useCallback(async () => {
    if (!composedKey) return;

    if (composedExists) {
      bindTo(composedKey);
      return;
    }

    setSaving(true);
    const saved = await saveTranslation(
      composedKey,
      changedWording(composedKey),
    );
    setSaving(false);
    if (!saved) return;

    onChange?.(bindingExpression(composedKey));
    onEndEditRun?.();
  }, [
    composedKey,
    composedExists,
    bindTo,
    saveTranslation,
    changedWording,
    onChange,
    onEndEditRun,
  ]);

  const handleSaveWording = useCallback(async () => {
    if (binding.kind !== 'bound') return;
    setSaving(true);
    await saveTranslation(binding.key, changedWording(binding.key));
    setSaving(false);
  }, [binding, saveTranslation, changedWording]);

  /** Back to this template's own words, keeping what the key said. */
  const handleUnbind = useCallback(() => {
    onChange?.(resolved.text);
    onEndEditRun?.();
    setOpen(false);
  }, [onChange, onEndEditRun, resolved.text]);

  const keyOptions = useMemo(
    () =>
      takenKeys.map((key) => ({
        value: key,
        label: (
          <TranslationKeyOption
            translationKey={key}
            text={resolveTranslations(translations, language)[key]}
          />
        ),
        searchText: translationSearchText(
          key,
          resolveTranslations(translations, language)[key],
        ),
      })),
    [takenKeys, translations, language],
  );

  const groupOptions = useMemo(
    () => translationGroups(translations).map((each) => ({ value: each })),
    [translations],
  );

  const state =
    binding.kind !== 'bound'
      ? 'literal'
      : resolved.missing
      ? 'missing'
      : 'bound';

  /** The language on screen first: it is the one being looked at. */
  const orderedLanguages = useMemo(
    () => [language, ...languages.filter((code) => code !== language)],
    [language, languages],
  );

  const popoverBody = (
    <div className={styles.popover}>
      <div className={styles.popoverRow}>
        <span className={styles.popoverLabel}>
          <F
            id="pages.emailCenter.translationField.key"
            defaultMessage="Translation key"
          />
        </span>
        {/* Bound: the key is the answer to "where do these words come from", so it is
            shown, not hidden in a tooltip. The picker below re-points the field. */}
        {binding.kind === 'bound' && (
          <code className={styles.keyName}>{binding.key}</code>
        )}

        {/* Not yet bound: group and key are chosen, not minted. Group is a combobox over
            the partner's existing groups that also accepts a new one — grouping is how a
            catalog of hundreds of keys stays navigable, so it should be a deliberate
            choice rather than everything landing in `emails`. */}
        {binding.kind !== 'bound' && (
          <>
            <div className={styles.keyComposer}>
              <AutoComplete
                size="small"
                className={styles.keyGroup}
                value={group}
                options={groupOptions}
                filterOption={(input, option) =>
                  String(option?.value ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={setGroup}
                placeholder={DEFAULT_GROUP}
              />
              <span className={styles.keyDot}>.</span>
              <Input
                size="small"
                value={keyName}
                onChange={(event) => setKeyName(event.target.value)}
                placeholder={getIntlTranslation(intl, {
                  id: 'pages.emailCenter.translationField.keyName',
                  defaultMessage: 'key name',
                })}
              />
            </div>
            {composedKey && (
              <span
                className={styles.keyPreview}
                data-exists={composedExists ? '1' : undefined}
              >
                <code>{composedKey}</code>
                {composedExists && (
                  <>
                    {' — '}
                    <F
                      id="pages.emailCenter.translationField.keyExists"
                      defaultMessage="already exists; this field will use its text"
                    />
                  </>
                )}
              </span>
            )}
          </>
        )}

        {canRepoint && (
          <Select
            size="small"
            className={styles.keySelect}
            showSearch
            value={null}
            placeholder={getIntlTranslation(
              intl,
              binding.kind === 'bound'
                ? {
                    id: 'pages.emailCenter.translationField.changeKey',
                    defaultMessage: 'Point at a different text…',
                  }
                : {
                    id: 'pages.emailCenter.translationField.pickKey',
                    defaultMessage: 'Use an existing text…',
                  },
            )}
            options={keyOptions}
            filterOption={(input, option) =>
              (option?.searchText ?? '').includes(input.toLowerCase())
            }
            onSelect={bindTo}
          />
        )}
      </div>

      <div className={styles.popoverRow}>
        <span className={styles.popoverLabel}>
          <F
            id="pages.emailCenter.translationField.allLanguages"
            defaultMessage="Wording, per language"
          />
        </span>
        <div className={styles.languageList}>
          {orderedLanguages.map((code) => (
            <div key={code} className={styles.languageRow}>
              <span
                className={styles.languageCode}
                data-empty={draft[code] ? undefined : '1'}
              >
                {languageLabel(code)}
              </span>
              <VariableTextField
                size="small"
                value={draft[code] ?? ''}
                placeholder={placeholder}
                variableGroups={wordingVariableGroups}
                onChange={(text) => setWording(code, text)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* The one thing to understand before typing here. */}
      <span className={styles.popoverHint}>
        <F
          id="pages.emailCenter.translationField.sharedHint"
          defaultMessage="This text is shared: changing it changes every email that uses the same key."
        />
      </span>

      <Space size={8} className={styles.popoverActions}>
        {binding.kind === 'bound' ? (
          <>
            {canEditWording && (
              <Button
                size="small"
                type="primary"
                loading={saving}
                onClick={handleSaveWording}
              >
                <F
                  id="pages.emailCenter.translationField.saveWording"
                  defaultMessage="Save text"
                />
              </Button>
            )}
            {canRepoint && (
              <Button size="small" onClick={handleUnbind}>
                <F
                  id="pages.emailCenter.translationField.unbind"
                  defaultMessage="Use plain text instead"
                />
              </Button>
            )}
          </>
        ) : (
          <Button
            size="small"
            type="primary"
            loading={saving}
            disabled={!composedKey || !canRepoint}
            onClick={handleCreate}
          >
            {composedExists ? (
              <F
                id="pages.emailCenter.translationField.useExisting"
                defaultMessage="Use this text"
              />
            ) : (
              <F
                id="pages.emailCenter.translationField.create"
                defaultMessage="Translate this field"
              />
            )}
          </Button>
        )}
      </Space>
    </div>
  );

  /**
   * `componentDisabled={false}` on purpose. The host renders this field inside a
   * `<Form disabled>` when the template may not be edited, and that context reaches the
   * popover through its portal — which would disable the per-language inputs and Save.
   * Those edit the partner's translation, not the template (see `disabled` above).
   */
  const popover = (
    // `componentDisabled` follows the *wording* permission, not the template's:
    // someone who may read a template but not its shared text sees the values and
    // no way to change them, rather than fields that discard what they type.
    <ConfigProvider componentDisabled={!canEditWording}>
      {popoverBody}
    </ConfigProvider>
  );

  /**
   * One set of handlers for both shapes of the plain-text field. Every event that
   * can move the caret re-reads it: the picker button inserts where it last was,
   * and the autocomplete needs to know whether it sits inside a `{{`.
   */
  const textFieldProps = {
    ref: inputRef,
    className: styles.input,
    'data-variables': offerVariables ? '1' : undefined,
    // Only ever rendered for a plain-text value; a bound field shows its wording.
    value: binding.kind === 'bound' ? '' : binding.text,
    placeholder,
    disabled,
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      onChange?.(event.target.value);
      rememberSelection();
      syncTrigger(event.target);
    },
    onKeyDown: handleKeyDown,
    onSelect: (
      event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      rememberSelection();
      syncTrigger(event.currentTarget);
    },
    onKeyUp: (
      event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      rememberSelection();
      syncTrigger(event.currentTarget);
    },
    onClick: (
      event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      rememberSelection();
      syncTrigger(event.currentTarget);
    },
    onBlur: () => {
      rememberSelection();
      variables.close();
      onEndEditRun?.();
    },
  };

  const iconTitle =
    state === 'literal'
      ? getIntlTranslation(intl, {
          id: 'pages.emailCenter.translationField.stateLiteral',
          defaultMessage:
            'This text is only in this email. Click to translate it.',
        })
      : state === 'missing'
      ? getIntlTranslation(intl, {
          id: 'pages.emailCenter.translationField.stateMissing',
          defaultMessage: 'No text saved for this language yet.',
        })
      : getIntlTranslation(intl, {
          id: 'pages.emailCenter.translationField.stateBound',
          defaultMessage: 'Shared text, translated per language.',
        });

  /**
   * One popover, not one per trigger. The icon and the translated display both open it,
   * so they are wrapped as a single trigger — rendering the popover twice with a shared
   * `open` gave two stacked copies of it.
   */
  const icon = (
    <Tooltip title={iconTitle}>
      <Button
        className={styles.prefix}
        data-state={state}
        size="small"
        /* Bound: the popover is how a translation gets filled in, which is allowed
           whoever owns the template. Literal: everything it offers would rewrite the
           field, so there is nothing to open. */
        disabled={disabled && binding.kind !== 'bound'}
        icon={<TranslationOutlined />}
      />
    </Tooltip>
  );

  return (
    <div className={styles.control}>
      <div className={styles.field}>
        <Popover
          open={open}
          onOpenChange={handleOpenChange}
          trigger="click"
          placement="bottomLeft"
          content={popover}
        >
          {binding.kind === 'bound' ? (
            /* Deliberately not an input: these words belong to the key, not to
               this field. The whole block opens the editor, so the wording and the
               key it came from are both targets as well as labels. */
            <div className={styles.boundTrigger}>
              {icon}
              {/* Nothing for this language: the binding takes the wording's place and
                  the strip below says it is missing. */}
              <TranslationDisplay
                text={resolved.missing ? undefined : resolved.text}
                fallback={bindingExpression(binding.key, binding.raw)}
                keys={resolved.missing ? [] : [binding.key]}
                clickable
              />
            </div>
          ) : (
            icon
          )}
        </Popover>

        {binding.kind !== 'bound' && (
          /* Anchored to the field, opened by the text rather than by a click —
             hence no trigger of its own. */
          <Popover
            open={variables.open}
            trigger={[]}
            arrow={false}
            placement="bottomLeft"
            content={
              <div className={styles.suggestions}>
                <VariableOptionList
                  options={variables.suggestions}
                  activeIndex={variables.activeIndex}
                  onPick={variables.accept}
                />
              </div>
            }
          >
            <div className={styles.inputWrap}>
              {rows ? (
                <Input.TextArea rows={rows} {...textFieldProps} />
              ) : (
                <Input {...textFieldProps} />
              )}
            </div>
          </Popover>
        )}

        {/* The end of the field, so it reads as an action on what was just typed —
            and out of the label, which the translation affordance already owns. */}
        {offerVariables && (
          <VariableInsertButton
            variableGroups={variableGroups}
            onInsert={handleInsertVariable}
          />
        )}
      </div>

      {/* Only the missing state has something to say: a bound field with wording
          already shows it, and a literal one has no translation to report. */}
      {state === 'missing' && (
        <TranslationHint
          actionLabel={
            <F
              id="pages.emailCenter.templates.resolvedHint.add"
              defaultMessage="Add translation"
            />
          }
          onAction={openEditor}
        >
          <F
            id="pages.emailCenter.templates.resolvedHint.missing"
            defaultMessage="No saved text for this language yet."
          />
        </TranslationHint>
      )}
    </div>
  );
};

export default React.memo(InputWithPartnerTranslation);
